<script setup lang="ts">
import UnifiedPublicActivityPage from '~/components/Unified/IA/UnifiedPublicActivityPage.vue';
import { useDevModeGate } from '~/composables/unified/DevPanel/useDevModeGate';
import { IaPageAudience } from '~/composables/unified/useIaAudience';
import { useLearnCourseSeo } from '~/composables/unified/useLearnCourseSeo';
import { assetQueryDocument, buildAssetQueryVariables } from '~/composables/unified/useLearningAssetFlow';
import { usePublicActivityPage } from '~/composables/unified/usePublicActivityPage';
import { usePublicResourcePage } from '~/composables/unified/usePublicResourcePage';
import { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import { UNIFIED_ROUTE_LEARN_ASSET } from '~/constants/routes/unified';
import { publicFileFragment } from '~/graphql/documents/core';
import { seoMetaFragment } from '~/graphql/documents/learningContent';
import { useFragment } from '~/graphql/generated';
import { richTextToPlainText } from '~/utils/richTextToPlainText';
import { stripBrandSuffix } from '~/utils/seo';

definePageMeta({
  authType: [AUTH_ROUTE_TYPE_NONE],
  // A signed-in student under the new information architecture opens it in the coach shell.
  ia: { audience: IaPageAudience.All, on: '/coach/learn/asset/:laSlug' },
});

const { t } = useI18n();
const route = useRoute();
const slug = computed(() => route.params.laSlug as string);

// Server-render the learning-asset Overview: await the public query so the graphcache +
// ssrExchange are primed before render. The meta description lives on the asset's seoMeta
// fragment as a ProseMirror rich-text doc, flattened to plain text.
const { $urqlClient } = useNuxtApp();
const { row, meta } = await usePublicResourcePage({
  key: () => `la:${slug.value}`,
  query: () => $urqlClient
    .query(assetQueryDocument, buildAssetQueryVariables(slug.value, { bySlug: true }))
    .toPromise(),
  selectRow: data => data?.learningAssets?.data?.[0] ?? null,
  // Prefer the CMS SEO title/description editors write for search, falling back to the name.
  // CMS titles carry the "| Anyway" brand that the global title template re-appends — strip it.
  name: (row) => {
    const meta = row.meta ? useFragment(seoMetaFragment, row.meta) : null;
    return stripBrandSuffix(meta?.title) || row.name;
  },
  description: (row) => {
    const meta = row.meta ? useFragment(seoMetaFragment, row.meta) : null;
    return richTextToPlainText(meta?.description);
  },
  image: (row) => {
    const cover = row.coverImage;
    return cover ? useFragment(publicFileFragment, cover)?.publicUrl ?? undefined : undefined;
  },
  notFound: t('unified.learn.assetNotFound'),
});

// Course + BreadcrumbList structured data (SSR): each asset is a self-contained learning unit,
// templated as a Course; its workload is its own duration. `dropParentBreadcrumbSegment` removes
// the /learn/asset crumb, which has no index page. See useLearnCourseSeo.
useLearnCourseSeo({
  row,
  meta,
  path: () => UNIFIED_ROUTE_LEARN_ASSET(slug.value),
  workloadMinutes: () => row.value?.duration ?? 0,
  dropParentBreadcrumbSegment: true,
});

usePublicActivityPage();

const isDevMode = await useDevModeGate();
provide('isDevMode', isDevMode);
</script>

<template>
  <div>
    <UnifiedPublicActivityPage>
      <UnifiedActivitiesLearningAssetActivity
        :activity-args="{ learningAssetSlug: slug, origin: AnalyticsOrigin.PublicBrowse }"
        :is-dev-mode="isDevMode"
      />
    </UnifiedPublicActivityPage>

    <ClientOnly>
      <UnifiedDevPanel v-if="isDevMode" />
    </ClientOnly>
  </div>
</template>
